import { supabase } from '@/lib/supabase';
import { TransactionB2C, TransactionStatus } from '@/types';

export interface CreateTransactionB2CData {
  display_id: string;
  customer_contact_id?: string;
  source: string;
  price: number;
  product?: Record<string, any>;
  sale_date: string;
  payment_method: string;
  status?: TransactionStatus;
  external_transaction_id?: string;
  payment_gateway?: string;
  installments?: number;
  notes?: string;
}

export interface TransactionFilters {
  search?: string;
  status?: TransactionStatus | 'all';
  dateFrom?: Date;
  dateTo?: Date;
  source?: string;
  payment_method?: string;
}

export class TransactionsService {
  /**
   * Fetch all transactions with optional filters
   */
  static async getTransactions(filters?: TransactionFilters): Promise<TransactionB2C[]> {
    try {
      let query = supabase
        .from('transactions_b2c')
        .select(`
          *,
          customer_contacts:customer_contact_id (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .order('sale_date', { ascending: false });

      // Apply filters
      if (filters) {
        if (filters.status && filters.status !== 'all') {
          query = query.eq('status', filters.status);
        }

        if (filters.dateFrom) {
          query = query.gte('sale_date', filters.dateFrom.toISOString());
        }

        if (filters.dateTo) {
          query = query.lte('sale_date', filters.dateTo.toISOString());
        }

        if (filters.source) {
          query = query.eq('source', filters.source);
        }

        if (filters.payment_method) {
          query = query.eq('payment_method', filters.payment_method);
        }

        if (filters.search) {
          query = query.or(`display_id.ilike.%${filters.search}%,notes.ilike.%${filters.search}%,external_transaction_id.ilike.%${filters.search}%,payment_method.ilike.%${filters.search}%,source.ilike.%${filters.search}%`);
        }
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching transactions:', error);
        throw new Error(`Failed to fetch transactions: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error in getTransactions:', error);
      throw error;
    }
  }

  /**
   * Get a single transaction by ID
   */
  static async getTransactionById(id: string): Promise<TransactionB2C | null> {
    try {
      const { data, error } = await supabase
        .from('transactions_b2c')
        .select(`
          *,
          customer_contacts:customer_contact_id (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Not found
        }
        console.error('Error fetching transaction:', error);
        throw new Error(`Failed to fetch transaction: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error in getTransactionById:', error);
      throw error;
    }
  }

  /**
   * Create a new transaction
   */
  static async createTransaction(transactionData: CreateTransactionB2CData): Promise<TransactionB2C> {
    try {
      const { data, error } = await supabase
        .from('transactions_b2c')
        .insert([{
          ...transactionData,
          status: transactionData.status || 'pending'
        }])
        .select(`
          *,
          customer_contacts:customer_contact_id (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .single();

      if (error) {
        console.error('Error creating transaction:', error);
        throw new Error(`Failed to create transaction: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error in createTransaction:', error);
      throw error;
    }
  }

  /**
   * Update a transaction
   */
  static async updateTransaction(id: string, updates: Partial<CreateTransactionB2CData>): Promise<TransactionB2C> {
    try {
      const { data, error } = await supabase
        .from('transactions_b2c')
        .update(updates)
        .eq('id', id)
        .select(`
          *,
          customer_contacts:customer_contact_id (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .single();

      if (error) {
        console.error('Error updating transaction:', error);
        throw new Error(`Failed to update transaction: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error in updateTransaction:', error);
      throw error;
    }
  }

  /**
   * Delete a transaction
   */
  static async deleteTransaction(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('transactions_b2c')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting transaction:', error);
        throw new Error(`Failed to delete transaction: ${error.message}`);
      }
    } catch (error) {
      console.error('Error in deleteTransaction:', error);
      throw error;
    }
  }

  /**
   * Get transaction statistics
   */
  static async getTransactionStats(filters?: Pick<TransactionFilters, 'dateFrom' | 'dateTo'>): Promise<{
    totalCount: number;
    completedCount: number;
    pendingCount: number;
    failedCount: number;
    refundedCount: number;
    totalAmount: number;
    pendingAmount: number;
    refundedAmount: number;
  }> {
    try {
      let query = supabase
        .from('transactions_b2c')
        .select('status, price');

      // Apply date filters if provided
      if (filters?.dateFrom) {
        query = query.gte('sale_date', filters.dateFrom.toISOString());
      }

      if (filters?.dateTo) {
        query = query.lte('sale_date', filters.dateTo.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching transaction stats:', error);
        throw new Error(`Failed to fetch transaction stats: ${error.message}`);
      }

      const transactions = data || [];
      
      const completed = transactions.filter(t => t.status === 'completed');
      const pending = transactions.filter(t => t.status === 'pending');
      const failed = transactions.filter(t => t.status === 'failed');
      const refunded = transactions.filter(t => t.status === 'refunded');
      
      const totalAmount = completed.reduce((sum, t) => sum + Number(t.price), 0);
      const pendingAmount = pending.reduce((sum, t) => sum + Number(t.price), 0);
      const refundedAmount = refunded.reduce((sum, t) => sum + Number(t.price), 0);
      
      return {
        totalCount: transactions.length,
        completedCount: completed.length,
        pendingCount: pending.length,
        failedCount: failed.length,
        refundedCount: refunded.length,
        totalAmount,
        pendingAmount,
        refundedAmount,
      };
    } catch (error) {
      console.error('Error in getTransactionStats:', error);
      throw error;
    }
  }

  /**
   * Generate a unique display ID for new transactions
   */
  static generateDisplayId(): string {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `TXN-${timestamp}-${random}`.toUpperCase();
  }

  /**
   * Get customer contacts for dropdown selection
   */
  static async getCustomerContacts(): Promise<Array<{
    id: string;
    first_name: string;
    last_name?: string;
    email: string;
  }>> {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('id, first_name, last_name, email')
        .order('first_name', { ascending: true });

      if (error) {
        console.error('Error fetching customer contacts:', error);
        throw new Error(`Failed to fetch customer contacts: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error in getCustomerContacts:', error);
      throw error;
    }
  }
}

export default TransactionsService;
